import KeyboardDoubleArrowLeftRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowLeftRounded';
import KeyboardDoubleArrowRightRoundedIcon from '@mui/icons-material/KeyboardDoubleArrowRightRounded';
import { Alert, Box, Button, ButtonBase, CircularProgress, IconButton, Stack, SxProps, Theme, Tooltip } from '@mui/material';
import React from 'react';
import { BLUE, BLUE_TEXT, BORDER, INTER, NAVY, SLATE, SOFT_BLUE } from '../style/verbaScholaStyles';

export interface BuilderFrame {
    title: string;
    refreshLabel: string;
    refreshDisabled: boolean;
    onRefresh: () => void;
    /** Set by the plan page from its live plan; other pages leave it to the shell's read. */
    exercisesLockReason?: string | null;
}

export const BuilderFrameContext = React.createContext<React.Dispatch<React.SetStateAction<BuilderFrame>> | null>(null);

/** Keep the shared header in sync with the active page without remounting the shell. */
export function useBuilderFrame(frame: BuilderFrame) {
    const setFrame = React.useContext(BuilderFrameContext);
    const latest = React.useRef(frame);
    latest.current = frame;
    React.useLayoutEffect(() => {
        setFrame?.({
            title: frame.title,
            refreshLabel: frame.refreshLabel,
            refreshDisabled: frame.refreshDisabled,
            onRefresh: () => latest.current.onRefresh(),
            exercisesLockReason: frame.exercisesLockReason,
        });
    }, [setFrame, frame.title, frame.refreshLabel, frame.refreshDisabled, frame.exercisesLockReason]);
}

/** A wide-screen side panel that folds into a rail. Below its breakpoint each panel is a drawer instead. */
export type BuilderSidePanel = 'copilot' | 'structure';
/** Width of the copilot column on wide screens. */
export const BUILDER_COPILOT_WIDTH = 360;
/** Width of a folded panel's rail. */
export const BUILDER_RAIL_WIDTH = 44;
const PANELS_STORAGE_KEY = 'verbaschola:lesson-builder-panels';
// Each panel is a column from its own breakpoint up: the copilot from lg, the structure from md.
const PANEL_COLUMN_QUERY: Record<BuilderSidePanel, string> = { copilot: '(min-width: 1200px)', structure: '(min-width: 900px)' };

/** Eases a fold or unfold: the grid's columns and the content's width cap move together. Only a
 *  `no-preference` query adds it, so spreading this never replaces a caller's own reduced-motion rules. */
export function builderPanelResize(property: 'grid-template-columns' | 'max-width') {
    return { '@media (prefers-reduced-motion: no-preference)': { transition: `${property} 200ms ease` } };
}

interface BuilderPanels {
    open: Record<BuilderSidePanel, boolean>;
    setOpen: (panel: BuilderSidePanel, open: boolean) => void;
    /** Unfolds a panel for a page action only while it is a wide-screen column. In a narrow
     *  window the page opens its drawer instead, and the saved wide-screen choice stays as it was. */
    reveal: (panel: BuilderSidePanel) => void;
}

const BuilderPanelsContext = React.createContext<BuilderPanels | null>(null);

function savedPanels(): Record<BuilderSidePanel, boolean> {
    try {
        const saved = JSON.parse(window.localStorage.getItem(PANELS_STORAGE_KEY) ?? '{}');
        return { copilot: saved?.copilot !== false, structure: saved?.structure !== false };
    } catch {
        return { copilot: true, structure: true };
    }
}

/** One open/folded choice per panel, shared by the three builder pages and kept for later visits. */
export function BuilderPanelsProvider({ children }: { children: React.ReactNode }) {
    const [open, setOpenState] = React.useState(savedPanels);
    React.useEffect(() => {
        try { window.localStorage.setItem(PANELS_STORAGE_KEY, JSON.stringify(open)); } catch { /* Blocked storage keeps the choice for this visit only. */ }
    }, [open]);
    const setOpen = React.useCallback((panel: BuilderSidePanel, value: boolean) =>
        setOpenState(current => current[panel] === value ? current : { ...current, [panel]: value }), []);
    const reveal = React.useCallback((panel: BuilderSidePanel) => {
        if (window.matchMedia(PANEL_COLUMN_QUERY[panel]).matches) setOpen(panel, true);
    }, [setOpen]);
    const panels = React.useMemo(() => ({ open, setOpen, reveal }), [open, setOpen, reveal]);
    return <BuilderPanelsContext.Provider value={panels}>{children}</BuilderPanelsContext.Provider>;
}

export function useBuilderPanels(): BuilderPanels {
    const panels = React.useContext(BuilderPanelsContext);
    if (!panels) throw new Error('useBuilderPanels is only available inside the lesson builder.');
    return panels;
}

/** After a fold or unfold, focus the control that replaced the pressed one and ease the shown panel or rail in from its edge. */
function afterPanelToggle(panel: BuilderSidePanel, open: boolean) {
    requestAnimationFrame(() => {
        const control = document.querySelector<HTMLElement>(open ? `[data-builder-panel-hide="${panel}"]` : `[data-builder-panel-show="${panel}"]`);
        control?.focus({ preventScroll: true });
        const shown = open ? document.querySelector<HTMLElement>(`[data-builder-panel="${panel}"]`) : control;
        if (!shown?.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        const offset = panel === 'copilot' ? 16 : -16;
        shown.animate([{ opacity: 0, transform: `translateX(${offset}px)` }, { opacity: 1, transform: 'none' }], { duration: 160, easing: 'ease-out' });
    });
}

/** Folds a panel into its rail. It sits at the panel's outer top corner, where the rail's opener appears. */
export function BuilderPanelHideButton({ panel, title, disabled = false, sx }: {
    panel: BuilderSidePanel; title: string; disabled?: boolean; sx?: SxProps<Theme>;
}) {
    const { setOpen } = useBuilderPanels();
    const Icon = panel === 'copilot' ? KeyboardDoubleArrowRightRoundedIcon : KeyboardDoubleArrowLeftRoundedIcon;
    const label = `Hide ${title.toLowerCase()}`;
    return <Tooltip title={label} describeChild>
        <Box component="span" sx={[{ display: 'inline-flex', flexShrink: 0 }, ...(Array.isArray(sx) ? sx : [sx])]}>
            <IconButton data-builder-panel-hide={panel} aria-label={label} aria-expanded disabled={disabled}
                onClick={() => { setOpen(panel, false); afterPanelToggle(panel, false); }}
                sx={{ width: 36, height: 36, color: SLATE, '&:hover': { color: BLUE_TEXT, backgroundColor: SOFT_BLUE } }}>
                <Icon fontSize="small" />
            </IconButton>
        </Box>
    </Tooltip>;
}

/** A folded panel: a slim strip on the panel's edge that reopens it. A dot marks something waiting inside. */
export function BuilderPanelRail({ panel, title, icon, attention, sx }: {
    panel: BuilderSidePanel; title: string; icon: React.ReactNode;
    /** Why the folded panel needs a look, such as an unsent message or a running generation. */
    attention?: string | null;
    sx?: SxProps<Theme>;
}) {
    const { setOpen } = useBuilderPanels();
    const Icon = panel === 'copilot' ? KeyboardDoubleArrowLeftRoundedIcon : KeyboardDoubleArrowRightRoundedIcon;
    const label = `Show ${title.toLowerCase()}`;
    // The strip keeps its width at the panel's outer edge while the column eases narrower around it.
    return <Box sx={[{ minWidth: 0, alignSelf: 'stretch', display: 'flex', alignItems: 'flex-start', justifyContent: panel === 'copilot' ? 'flex-end' : 'flex-start' },
        ...(Array.isArray(sx) ? sx : [sx])]}>
        {/* The rail shows its name; a tooltip would pop up under the pointer after every fold, so it only explains the dot. */}
        <Tooltip title={attention ? `${label}: ${attention}` : ''} placement={panel === 'copilot' ? 'left' : 'right'} describeChild>
            <ButtonBase data-builder-panel-show={panel} aria-label={label} aria-expanded={false}
                onClick={() => { setOpen(panel, true); afterPanelToggle(panel, true); }}
                sx={{ position: 'sticky', top: 88, width: BUILDER_RAIL_WIDTH, flexShrink: 0, py: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25,
                    borderRadius: '14px', border: `1px solid ${BORDER}`, backgroundColor: '#fff', color: BLUE_TEXT,
                    '&:hover': { borderColor: BLUE, backgroundColor: SOFT_BLUE }, '&.Mui-focusVisible': { outline: `2px solid ${BLUE}`, outlineOffset: 2 } }}>
                <Icon fontSize="small" />
                {icon}
                <Box component="span" sx={{ writingMode: 'vertical-rl', fontFamily: INTER, fontWeight: 700, fontSize: 12.5, color: NAVY, whiteSpace: 'nowrap' }}>{title}</Box>
                {attention && <Box component="span" data-testid={`${panel}-rail-attention`} sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: BLUE }} />}
            </ButtonBase>
        </Tooltip>
    </Box>;
}

/** Stage-local loading and errors keep the nested editor mounted beneath its layout. */
export function BuilderPageStatus({ loading, error, onRetry, children }: {
    loading: boolean; error: string | null; onRetry?: () => void; children: React.ReactNode;
}) {
    return <>
        {error && <Alert severity="error" action={onRetry && <Button color="inherit" size="small" onClick={onRetry}>Refresh saved lesson</Button>} sx={{ fontFamily: INTER, mb: 2 }}>{error}</Alert>}
        {loading ? <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress size={28} aria-label="Loading lesson editor" /></Stack> : children}
    </>;
}
